import {
  Box,
  Button,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link as RouterLink } from 'react-router-dom';
import { MouseEvent, ReactNode, useState } from 'react';
import { SxProps } from '@mui/system';
import { MFSubmitButton } from '../../lib/formik';

export const userMgmtTableHeaderStyles: SxProps = {
  '.MuiTableCell-head': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#1F53BE',
    backgroundColor: '#F6F9FD',
    borderBottom: '1px solid #E8ECF0',
    whiteSpace: 'nowrap',
  },
};

export const userMgmtTableBodyStyles: SxProps = {
  '.MuiTableCell-root': {
    borderBottom: '1px solid #F2F4F7',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    color: '#111927',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
};

export const userMgmtRowStyles: SxProps = {
  background: 'transparent',
  ':hover': {
    bgcolor: '#F9FAFB',
  },
};

export const userMgmtNameLinkSx: SxProps = {
  color: '#111927',
  textDecoration: 'none',
  fontWeight: 400,
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
  lineHeight: 1.3,
  ':hover': {
    color: 'primary.main',
  },
};

export const userMgmtInviteButtonSx: SxProps = {
  color: '#FFFFFF',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: 1,
  letterSpacing: 0,
  textTransform: 'none',
  borderRadius: '8px',
  py: 1.25,
  px: 2.5,
  boxShadow: 'none',
  bgcolor: 'primary.main',
  whiteSpace: 'nowrap',
  '&:hover': {
    bgcolor: 'primary.dark',
    boxShadow: 'none',
  },
};

export const userMgmtSearchFieldSx: SxProps = {
  flex: 1,
  minWidth: { xs: '100%', sm: 280 },
  maxWidth: 420,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#E5E7EB',
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2057A6',
      borderRadius: '8px',
    },
  },
};

export const AccessToggle = ({
  checked,
  onChange,
  disabled,
  enabledLabel = 'Enabled',
  disabledLabel = 'Disabled',
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  enabledLabel?: string;
  disabledLabel?: string;
}): JSX.Element => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', minWidth: 'max-content' }}>
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      disableRipple
      sx={{
        width: 32,
        height: 18,
        p: 0,
        overflow: 'visible',
        '& .MuiSwitch-switchBase': {
          p: '2px',
          top: 0,
          left: 0,
          color: '#FFFFFF',
          '&.Mui-checked': {
            transform: 'translateX(14px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': {
              bgcolor: '#0DB975',
              opacity: 1,
              border: 'none',
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-disabled': {
            color: '#FFFFFF',
            '& + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
        },
        '& .MuiSwitch-thumb': {
          width: 14,
          height: 14,
          boxShadow: '0 1px 2px rgba(16, 24, 40, 0.1)',
          bgcolor: '#FFFFFF',
        },
        '& .MuiSwitch-track': {
          borderRadius: 9,
          bgcolor: '#D0D5DD',
          opacity: 1,
          transition: 'background-color 0.2s',
        },
      }}
    />
    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        fontWeight: 400,
        color: checked ? '#0DB975' : '#6C737F !important',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}>
      {checked ? enabledLabel : disabledLabel}
    </Typography>
  </Stack>
);

export type ActionMenuItem = {
  label: string;
  icon?: string;
  to?: { pathname: string; state?: Record<string, unknown> };
  onClick?: () => void;
};

const actionMenuItemSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  fontWeight: 400,
  color: '#6C737F',
  gap: 1,
  py: 1,
};

export const RowActionsMenu = ({ items }: { items: ActionMenuItem[] }): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderItemContent = (item: ActionMenuItem) => (
    <>
      {item.icon ? (
        <Box
          component="img"
          src={item.icon}
          alt=""
          sx={{ width: 16, height: 16, flexShrink: 0 }}
        />
      ) : null}
      {item.label}
    </>
  );

  return (
    <>
      <IconButton
        aria-label="Actions"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        sx={{ color: '#2057A6' }}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.12)',
            minWidth: 160,
          },
        }}>
        {items.map((item) =>
          item.to ? (
            <MenuItem
              key={item.label}
              component={RouterLink}
              to={item.to}
              onClick={handleClose}
              sx={actionMenuItemSx}>
              {renderItemContent(item)}
            </MenuItem>
          ) : (
            <MenuItem
              key={item.label}
              onClick={() => {
                item.onClick?.();
                handleClose();
              }}
              sx={actionMenuItemSx}>
              {renderItemContent(item)}
            </MenuItem>
          )
        )}
      </Menu>
    </>
  );
};

export const UserMgmtListToolbar = ({
  search,
  onSearchChange,
  searchPlaceholder,
  inviteLabel,
  onInvite,
  showInvite = true,
  extraStart,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  inviteLabel: string;
  onInvite?: () => void;
  showInvite?: boolean;
  extraStart?: ReactNode;
}): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      flexWrap: 'wrap',
      mb: 2.5,
    }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
      <TextField
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchSharpIcon sx={{ color: '#9CA3AF', fontSize: 20 }} aria-hidden="true" />
            </InputAdornment>
          ),
        }}
        sx={userMgmtSearchFieldSx}
      />
      {extraStart}
    </Box>
    {showInvite && onInvite && (
      <Button
        variant="contained"
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        sx={userMgmtInviteButtonSx}
        onClick={onInvite}>
        {inviteLabel}
      </Button>
    )}
  </Box>
);

export const userMgmtBoxStyles: SxProps = {
  minHeight: 'unset',
  maxHeight: 'none',
  width: '100%',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'visible',
  '& table': {
    width: '100%',
    minWidth: 800,
  },
};

export const UserMgmtFormActions = ({ onCancel }: { onCancel: () => void }): JSX.Element => (
  <Stack
    direction="row"
    spacing={2}
    justifyContent="center"
    alignItems="stretch"
    sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3, mb: 2 }}>
    <Button
      type="button"
      variant="outlined"
      onClick={onCancel}
      sx={{
        flex: 1,
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        fontWeight: 500,
        color: '#2057A6',
        borderColor: '#2057A6',
        textTransform: 'none',
        '&:hover': {
          borderColor: '#2057A6',
          bgcolor: 'rgba(32, 87, 166, 0.04)',
        },
      }}>
      Cancel
    </Button>
    <Box
      sx={{
        flex: 1,
        '& .MuiLoadingButton-root': {
          mt: 0,
          mb: 0,
          width: '100%',
          height: '100%',
        },
      }}>
      <MFSubmitButton label="Save" fullWidth />
    </Box>
  </Stack>
);

export const UserMgmtSuccessDialog = ({
  open,
  message,
  onGoToUserManagement,
}: {
  open: boolean;
  message: string;
  onGoToUserManagement: () => void;
}): JSX.Element => (
  <Dialog
    open={open}
    disableEscapeKeyDown
    PaperProps={{
      sx: {
        borderRadius: '24px',
        width: '100%',
        maxWidth: 360,
        m: 2,
        boxShadow: '0px 16px 40px rgba(15, 23, 42, 0.16)',
      },
    }}
    BackdropProps={{
      sx: { bgcolor: 'rgba(15, 23, 42, 0.45)' },
    }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        px: 3,
        pt: 4,
        pb: 3,
        gap: 2.5,
      }}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '20px',
          bgcolor: '#E7FAF2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          component="img"
          src="/images/success-illustration.svg"
          alt=""
          sx={{ width: 55, height: 55 }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 18,
          lineHeight: 1.25,
          color: '#111927',
          maxWidth: 240,
        }}>
        {message}
      </Typography>
      <Button
        fullWidth
        variant="contained"
        onClick={onGoToUserManagement}
        sx={{
          mt: 0.5,
          bgcolor: '#2057A6',
          color: '#FFFFFF',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: 1,
          letterSpacing: '0.02em',
          textTransform: 'none',
          borderRadius: '8px',
          py: 1.5,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#1A4A8C',
            boxShadow: 'none',
          },
        }}>
        Go to User Management
      </Button>
    </Box>
  </Dialog>
);
